import { Guide, StructureData, type ParamsWithLocale } from '@/lib/types';
import { AUTHORITY_CODE, createRandomId } from '@/lib/utils';
import { Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import axios from 'axios';
import clsx from 'clsx';
import { Fragment, useCallback, useMemo, useState } from 'react';
import GuideModal from './Modal';

type Props = ParamsWithLocale & {
  data: StructureData[][];
};

export default function Chart({ locale, data }: Props) {
  const [opened, { open, close }] = useDisclosure(false);
  const [guide, setGuide] = useState<Guide>();

  const getGuideInfo = useCallback(
    async function (id: number, title: string) {
      if (!id) return;

      try {
        const { data } = await axios.get('/authorities/lists/get-guide', {
          headers: {
            language: locale,
            code: AUTHORITY_CODE,
          },
          params: { id },
        });

        if (data?.length > 0) {
          open();
          setGuide({ ...data?.[0], position: title });
        }
      } catch (e) {
        close();
        console.log(e);
      }
    },
    [locale, open, setGuide, close]
  );

  const { primaryPart, secondaryPart, otherPart } = useMemo(
    () => ({ primaryPart: data?.[0] ?? [], secondaryPart: data?.[1] ?? [], otherPart: data?.slice(2) ?? [] }),
    [data]
  );

  return (
    <Fragment>
      <Box className="chart">
        <Box className="chart__row primary">
          {primaryPart?.map((item, itemIndex) => (
            <ChartItem
              key={item?.id}
              {...item}
              isPrimary={itemIndex === 0}
              lineClassname_1={itemIndex > 0 ? 'horizontal left' : 'vertical bottom'}
              handleClick={getGuideInfo}
            />
          ))}
          <span className="row__line" />
          <span className="row__hide-line" />
        </Box>

        <Box className="chart__row secondary">
          {secondaryPart?.map((item, itemIndex) => (
            <ChartItem
              key={item?.id}
              {...item}
              lineClassname_1="vertical top"
              lineClassname_2={itemIndex !== secondaryPart?.length - 1 ? 'horizontal to-right' : ''}
              lineClassname_3={itemIndex !== 0 ? 'horizontal to-left' : ''}
              hideLineClassname={clsx({
                'horizontal topLeft': itemIndex === 0,
                'horizontal topRight': itemIndex === secondaryPart?.length - 1,
              })}
              handleClick={getGuideInfo}
            />
          ))}
        </Box>

        {otherPart?.map((part, partIndex) => (
          <Box key={createRandomId()} className="chart__row">
            {part?.map((item, itemIndex) => (
              <ChartItem
                key={item?.id}
                {...item}
                isPrimary={itemIndex === 0}
                lineClassname_1="vertical top"
                lineClassname_2={itemIndex !== part?.length - 1 ? 'horizontal to-right' : ''}
                lineClassname_3={itemIndex !== 0 ? 'horizontal to-left' : ''}
                hideLineClassname={clsx({
                  'horizontal topLeft': itemIndex === 0,
                  'horizontal topRight': itemIndex === part?.length - 1,
                })}
                childRowLineClassname={!!part[itemIndex + 1] ? 'child-row__line' : ''}
                handleClick={getGuideInfo}
              />
            ))}
            <span className="row__line" />
            {partIndex === otherPart?.length - 1 ? <span className="row__hide-line" /> : null}
          </Box>
        ))}
      </Box>
      <GuideModal opened={opened} onClose={close} guide={guide} />
    </Fragment>
  );
}

type ChartItemProps = StructureData & {
  isPrimary?: boolean;
  lineClassname_1?: string;
  lineClassname_2?: string;
  lineClassname_3?: string;
  lineClassname_4?: string;
  hideLineClassname?: string;
  childRowLineClassname?: string;
  handleClick: (id: number, title: string) => void;
};

function ChartItem(props: ChartItemProps) {
  const {
    title,
    guide_id,
    children,
    isPrimary,
    lineClassname_1,
    lineClassname_2,
    lineClassname_3,
    lineClassname_4,
    hideLineClassname,
    childRowLineClassname,
    handleClick,
  } = props;

  return (
    <Box className={clsx('chart__item', { primary: isPrimary, 'pointer-events-none': !guide_id })}>
      <button type="button" onClick={() => handleClick(guide_id, title)}>
        {title}
        {lineClassname_1?.trim() ? <span className={clsx('chart__line', lineClassname_1)} /> : null}
        {lineClassname_2?.trim() ? <span className={clsx('chart__line', lineClassname_2)} /> : null}
        {lineClassname_3?.trim() ? <span className={clsx('chart__line', lineClassname_3)} /> : null}
        {lineClassname_4?.trim() ? <span className={clsx('chart__line', lineClassname_4)} /> : null}
        {hideLineClassname?.trim() ? <span className={clsx('chart__hide-line', hideLineClassname)} /> : null}
      </button>
      {children?.length > 0 ? (
        <Box className="child__row">
          {children.map((item, itemIndex) => (
            <ChartItem
              key={item?.id}
              {...item}
              lineClassname_1="vertical top sm"
              lineClassname_2={itemIndex !== children?.length - 1 ? 'horizontal to-right sm' : ''}
              lineClassname_3="horizontal to-left sm"
              childRowLineClassname="child-row__line sm"
              handleClick={handleClick}
            />
          ))}
          {childRowLineClassname?.trim() ? <span className={clsx('child-row__line', childRowLineClassname)} /> : null}
        </Box>
      ) : null}
    </Box>
  );
}
